import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { api } from '../lib/api';

const FACTOR_LABELS: Record<string, { label: string; color: string }> = {
  overdue: { label: 'Overdue Items', color: 'bg-red-500' },
  coverage: { label: 'Framework Coverage', color: 'bg-blue-500' },
  attestation: { label: 'Attestation Rate', color: 'bg-purple-500' },
  escalation: { label: 'Escalation Health', color: 'bg-orange-500' },
  profile: { label: 'Profile Completeness', color: 'bg-teal-500' },
};

export default function ComplianceScorePage() {
  const { data: scoreData, isLoading } = useQuery({
    queryKey: ['compliance-score'],
    queryFn: () => api.dashboard.score(),
  });

  const { data: historyData } = useQuery({
    queryKey: ['compliance-score-history'],
    queryFn: () => api.dashboard.scoreHistory(),
  });

  const score = scoreData?.data as Record<string, unknown> | undefined;
  const factors = score?.factors as Record<string, { score: number; max: number; [k: string]: unknown }> | undefined;
  const recommendations = (score?.recommendations as string[]) ?? [];
  const trend = score?.trend as string;
  const totalScore = (score?.score as number) ?? 0;
  const history = ((historyData?.data ?? []) as { score: number; calculatedAt: string }[]).slice(0, 90);

  const scoreColor = totalScore >= 80 ? 'text-green-400' : totalScore >= 50 ? 'text-yellow-400' : 'text-red-400';
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  if (isLoading) return <div className="p-6 text-gray-400">Loading score...</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Compliance Health Score</h1>
          <p className="text-sm text-gray-400 mt-1">Comprehensive breakdown of your compliance posture</p>
        </div>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded text-sm border border-gray-700"
          aria-label="Export score report">
          <Download className="w-4 h-4" /> Export PDF
        </button>
      </div>

      {/* Score header */}
      <div className="bg-gray-800/50 rounded-xl p-6 flex items-center gap-8">
        <div className="text-center">
          <div className={`text-6xl font-bold ${scoreColor}`}>{totalScore}</div>
          <div className="text-sm text-gray-400 mt-1">out of 100</div>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <TrendIcon className={`w-5 h-5 ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`} />
            <span className="text-sm text-gray-400">
              {trend === 'up' ? 'Improving' : trend === 'down' ? 'Declining' : 'Stable'} vs last week
            </span>
          </div>
          <div className={`text-sm font-medium ${totalScore >= 80 ? 'text-green-400' : totalScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
            {totalScore >= 80 ? 'Strong Compliance Posture' : totalScore >= 50 ? 'Moderate — Room for Improvement' : 'At Risk — Immediate Action Needed'}
          </div>
        </div>
      </div>

      {/* Factor breakdown */}
      <div className="bg-gray-800/50 rounded-xl p-6 space-y-4">
        <h2 className="text-white font-medium">Score Breakdown</h2>
        {factors && Object.entries(factors).map(([key, factor]) => {
          const config = FACTOR_LABELS[key];
          if (!config) return null;
          const pct = (factor.score / factor.max) * 100;
          return (
            <div key={key} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">{config.label}</span>
                <span className="text-gray-400">{factor.score}/{factor.max}</span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${pct > 70 ? 'bg-green-500' : pct > 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.max(pct, 2)}%` }} />
              </div>
              <div className="text-xs text-gray-500">
                {key === 'overdue' && `${(factor as Record<string, unknown>).overdueCount ?? 0} overdue items`}
                {key === 'coverage' && `${(factor as Record<string, unknown>).covered ?? 0}/${(factor as Record<string, unknown>).frameworks ?? 0} frameworks covered`}
                {key === 'attestation' && `${Math.round(((factor as Record<string, unknown>).rate as number ?? 0) * 100)}% completion rate`}
                {key === 'escalation' && `${(factor as Record<string, unknown>).openCount ?? 0} open escalations`}
                {key === 'profile' && `${Math.round(((factor as Record<string, unknown>).completeness as number ?? 0) * 100)}% complete`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 space-y-3">
          <h2 className="text-white font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400" /> How to Improve
          </h2>
          {recommendations.map((rec, i) => (
            <div key={i} className="flex items-start gap-3 text-sm">
              <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">{rec}</span>
            </div>
          ))}
        </div>
      )}

      {/* History chart (simple bar chart) */}
      {history.length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 space-y-3">
          <h2 className="text-white font-medium">Score History (Last 90 Days)</h2>
          <div className="flex items-end gap-px h-32">
            {history.map((h, i) => {
              const barColor = h.score >= 80 ? 'bg-green-500' : h.score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
              return (
                <div key={i} className="flex-1 flex flex-col justify-end group relative" title={`${new Date(h.calculatedAt).toLocaleDateString()}: ${h.score}`}>
                  <div className={`${barColor} rounded-t-sm min-h-[2px] transition-all hover:opacity-80`} style={{ height: `${h.score}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{history.length > 0 ? new Date(history[history.length - 1].calculatedAt).toLocaleDateString() : ''}</span>
            <span>Today</span>
          </div>
        </div>
      )}
    </div>
  );
}
